import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Send, MapPin, Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { API, bookOnVrbo } from "@/lib/booking";
import { PROPERTY } from "@/data/property";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", phone: "", message: "" };
const guestOptions = Array.from({ length: 18 }, (_, i) => i + 1);

function DateField({ label, date, setDate, testid, fromDate }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="mt-2 flex w-full items-center gap-2 rounded-xl border border-input bg-card px-4 py-3 text-left text-sm text-foreground transition-colors hover:border-accent"
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
            {date ? format(date, "MMM d, yyyy") : <span className="text-muted-foreground">Select date</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={(d) => d < (fromDate || new Date(new Date().setHours(0, 0, 0, 0)))}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function ContactForm() {
  const [form, setForm] = useState(emptyForm);
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [guests, setGuests] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so Michael can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
        guests: guests ? parseInt(guests, 10) : null,
        check_in: checkIn ? format(checkIn, "yyyy-MM-dd") : null,
        check_out: checkOut ? format(checkOut, "yyyy-MM-dd") : null,
      });
      toast.success("Request sent! Michael will get back to you shortly.");
      setForm(emptyForm);
      setCheckIn(undefined);
      setCheckOut(undefined);
      setGuests("");
    } catch (err) {
      toast.error("Something went wrong. Please try again or book directly on VRBO.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-background py-24 md:py-40 overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute top-8 right-4 md:right-12 font-display text-[9rem] md:text-[16rem] leading-none text-accent/10 select-none"
      >
        04
      </span>

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] text-accent font-medium mb-6">
              Chapter 04 — Plan Your Stay
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground text-balance">
              Check dates or <em className="italic font-light">message Michael.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-muted-foreground font-light max-w-lg">
              Send a quick request and the host will reply — or reserve instantly on VRBO. No hidden fees, ever.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="mt-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Name</Label>
                  <Input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Your name"
                    className="mt-2 rounded-xl border-input bg-card py-6" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Email</Label>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com"
                    className="mt-2 rounded-xl border-input bg-card py-6" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Phone (optional)</Label>
                  <Input data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000"
                    className="mt-2 rounded-xl border-input bg-card py-6" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Guests</Label>
                  <Select value={guests} onValueChange={setGuests}>
                    <SelectTrigger data-testid="guests-trigger" className="mt-2 rounded-xl border-input bg-card py-6">
                      <SelectValue placeholder="How many?" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {guestOptions.map((n) => (
                        <SelectItem key={n} value={String(n)} data-testid={`guest-option-${n}`}>
                          {n} {n === 1 ? "guest" : "guests"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <DateField label="Check-in" date={checkIn} setDate={setCheckIn} testid="checkin-trigger" />
                <DateField label="Check-out" date={checkOut} setDate={setCheckOut} testid="checkout-trigger" fromDate={checkIn} />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Message (optional)</Label>
                <Textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={4}
                  placeholder="Tell Michael about your trip — dates, group, questions..."
                  className="mt-2 rounded-xl border-input bg-card" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 text-sm font-medium tracking-wide text-accent-foreground transition-all duration-300 hover:gap-3.5 hover:bg-accent/90 disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send request"}
                <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.8} />
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={0.1}>
            <div className="rounded-[1.5rem] border border-border bg-secondary/40 p-8 md:p-10">
              <div className="flex items-center gap-2 text-accent">
                <MapPin className="h-5 w-5" strokeWidth={1.6} />
                <span className="text-xs uppercase tracking-[0.18em] font-medium">Where you'll be</span>
              </div>
              <h3 className="mt-4 font-display text-3xl md:text-4xl text-foreground">Graford, Texas</h3>
              <p className="mt-2 text-muted-foreground font-light">
                On the shoreline of {PROPERTY.region} — minutes from PK restaurants & shops, 15 minutes from Rocker B Ranch.
              </p>

              <div className="mt-8 space-y-3">
                {["Reserve instantly on VRBO", "All fees included — no surprises", "Rated 9.8/10 by 111 guests"].map((t) => (
                  <div key={t} className="flex items-center gap-3 text-foreground">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/12 text-accent">
                      <Check className="h-3.5 w-3.5" strokeWidth={2.2} />
                    </span>
                    <span className="font-light">{t}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-3 border-t border-border pt-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground font-display text-lg">MA</span>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Hosted by</p>
                  <p className="font-display text-xl text-foreground leading-tight">Michael Avidon</p>
                </div>
              </div>

              <button
                onClick={() => bookOnVrbo("contact")}
                data-testid="contact-book-button"
                className="mt-6 w-full group inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-medium tracking-wide text-primary-foreground transition-all duration-300 hover:gap-3.5 hover:bg-primary/90"
              >
                Book on VRBO
                <span aria-hidden className="transition-transform group-hover:translate-x-0.5">&rarr;</span>
              </button>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Possum+Kingdom+Lake+Graford+TX"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-map-link"
                className="mt-4 block text-center text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Open location in Google Maps
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
